import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Network } from 'lucide-react'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { listCandidates, listCoalitions, listParties, listSeatTypes } from './api'
import { PartyFormDialog } from './PartyFormDialog'
import { EntityChip, count, seatTypeLabel } from './vocab'

/** One page of each register is enough to draw the two panels; the counts are exact. */
const SCAN = 200

/**
 * The register at a glance.
 *
 * ## Exact counts come from the server, derived ones say what they are derived from
 *
 * Every headline count is a `totalElements` from the server — including the ACTIVE
 * ones, which are a size-1 request with the filter applied rather than a tally of
 * whatever this page happened to fetch. The two breakdowns below cannot be asked
 * for that way (there is no "candidates with no party" filter and no group-by
 * endpoint), so they are computed from the first {@link SCAN} rows and SAY SO the
 * moment there are more than that. A number whose base is unstated is the defect
 * this feature was built to avoid.
 *
 * ## The empty state is the common case
 *
 * The register ships with no seed data at all — no parties, no coalitions, no
 * candidates. A fresh deployment lands here first, so the empty state carries the
 * action rather than an apology.
 */
export function OverviewPage() {
  const canRead = useCan('politics:read')
  const canWriteParty = useCan('politics:party')
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  const parties = useLoad(() => listParties({}, 0, SCAN), [])
  const coalitions = useLoad(() => listCoalitions({}, 0, SCAN), [])
  const candidates = useLoad(() => listCandidates({}, 0, SCAN), [])
  const seatTypes = useLoad(() => listSeatTypes(), [])
  // Size 1: only the count is wanted, and the server counts better than this page can.
  const activeParties = useLoad(() => listParties({ status: 'ACTIVE' }, 0, 1), [])
  const activeCoalitions = useLoad(() => listCoalitions({ status: 'ACTIVE' }, 0, 1), [])

  if (!canRead) return <Forbidden what="the political register" />

  const partyRows = parties.data?.content ?? []
  const coalitionRows = coalitions.data?.content ?? []
  const candidateRows = candidates.data?.content ?? []

  const partyTotal = parties.data?.totalElements ?? null
  const coalitionTotal = coalitions.data?.totalElements ?? null
  const candidateTotal = candidates.data?.totalElements ?? null

  // Whether the derived panels are reading the whole register or a slice of it.
  const candidatesScanned = candidateTotal !== null && candidateTotal <= candidateRows.length
  const independents = candidateRows.filter((c) => !c.party).length

  const membersByCoalition = new Map<string, typeof partyRows>()
  let unaligned = 0
  for (const p of partyRows) {
    const cid = p.coalition?.coalitionId
    if (!cid) { unaligned += 1; continue }
    membersByCoalition.set(cid, [...(membersByCoalition.get(cid) ?? []), p])
  }

  const bySeat = new Map<string, number>()
  for (const c of candidateRows) {
    bySeat.set(c.seatTypeCode, (bySeat.get(c.seatTypeCode) ?? 0) + 1)
  }
  // Server list first, so a seat type nobody is standing for still shows as zero —
  // and a code the label map has never seen still gets a row, under its own code.
  const seatRows = [
    ...(seatTypes.data ?? []).map((s) => ({
      code: s.code, level: s.requiredUnitLevel, n: bySeat.get(s.code) ?? 0,
    })),
    ...[...bySeat.keys()]
      .filter((code) => !(seatTypes.data ?? []).some((s) => s.code === code))
      .map((code) => ({ code, level: null, n: bySeat.get(code) ?? 0 })),
  ]

  const loading = parties.loading && coalitions.loading && candidates.loading
    && partyRows.length === 0 && coalitionRows.length === 0 && candidateRows.length === 0

  const empty = partyTotal === 0 && coalitionTotal === 0 && candidateTotal === 0

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Political landscape"
        description="Parties, the alliances they sit in, and who is standing under them."
      />

      <ErrorStrip message={parties.error ?? coalitions.error ?? candidates.error ?? seatTypes.error} />

      {loading ? <LoadingBlock label="Loading the register…" /> : empty ? (
        <Panel bodyClassName="p-0">
          <EmptyState
            icon={<Network className="h-4 w-4" />}
            title="The register is empty"
            hint="Nothing is seeded — no parties, no coalitions, no candidates. Start with a party:
                  coalitions are made of them, candidates stand for them, and every colour on every
                  chart in the Polling Studio comes from one."
            action={canWriteParty
              ? (
                <button type="button" className="erp-btn erp-btn-primary"
                  onClick={() => setCreating(true)}>
                  Add the first party
                </button>
              )
              : (
                <span className="text-erp-xs text-muted-foreground">
                  Adding one needs the <code>politics:party</code> authority.
                </span>
              )}
          />
        </Panel>
      ) : (
        <>
          <KpiStrip>
            <MetricCard
              label="Parties"
              value={count(partyTotal)}
              sub={activeParties.data
                ? `${count(activeParties.data.totalElements)} active`
                : 'counting the active ones…'}
            />
            <MetricCard
              label="Coalitions"
              value={count(coalitionTotal)}
              sub={activeCoalitions.data
                ? `${count(activeCoalitions.data.totalElements)} active`
                : 'counting the active ones…'}
            />
            <MetricCard label="Candidates" value={count(candidateTotal)} sub="on the register" />
            <MetricCard
              label="Independents"
              // Not askable of the server — there is no "no party" filter — so it
              // is counted here, and withheld rather than guessed when this page
              // holds only part of the register.
              value={candidatesScanned ? count(independents) : '—'}
              sub={candidatesScanned
                ? 'standing with no party'
                : `not counted: ${count(candidateTotal)} candidates, ${candidateRows.length} read here`}
            />
            <MetricCard
              label="Parties outside a coalition"
              value={partyTotal !== null && partyTotal <= partyRows.length ? count(unaligned) : '—'}
              sub={partyTotal !== null && partyTotal <= partyRows.length
                ? 'no open membership'
                : `not counted: ${count(partyTotal)} parties, ${partyRows.length} read here`}
            />
          </KpiStrip>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 xl:col-span-7">
              <Panel
                title="Coalitions and their parties"
                description="Member parties in their own colours, with the candidates who reach each alliance."
                right={<Link className="erp-link text-erp-xs" to="/political-landscape/coalitions">All coalitions</Link>}
                bodyClassName="p-0"
              >
                <div className="divide-y divide-border">
                  {coalitionRows.length === 0 ? (
                    <p className="px-4 py-6 text-erp-sm text-muted-foreground">
                      No coalitions. Parties stand on their own until an alliance is recorded.
                    </p>
                  ) : coalitionRows.map((c) => {
                    const members = membersByCoalition.get(c.id) ?? []
                    return (
                      <div key={c.id} className="px-4 py-3 space-y-1.5">
                        <div className="flex items-baseline gap-2">
                          <EntityChip entity={c} />
                          <span className="text-erp-xs text-muted-foreground truncate">{c.name}</span>
                          <span className="ml-auto text-erp-xs tabular-nums whitespace-nowrap">
                            {count(c.candidateCount)} candidate{c.candidateCount === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1">
                          {members.length === 0 ? (
                            <span className="text-erp-xs text-muted-foreground">
                              No party currently holds an open membership
                            </span>
                          ) : members.map((p) => <EntityChip key={p.id} entity={p} />)}
                        </div>
                      </div>
                    )
                  })}

                  {unaligned > 0 && (
                    <div className="px-4 py-3">
                      <div className="text-erp-xs text-muted-foreground mb-1.5">
                        Independent of any coalition
                      </div>
                      <div className="flex flex-wrap items-center gap-1">
                        {partyRows.filter((p) => !p.coalition).map((p) => (
                          <EntityChip key={p.id} entity={p} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Panel>
            </div>

            <div className="col-span-12 xl:col-span-5">
              <Panel
                title="Candidates by seat"
                description="Seat types as the server publishes them, with the unit level each one contests."
                right={<Link className="erp-link text-erp-xs" to="/political-landscape/candidates">All candidates</Link>}
                bodyClassName="p-0"
              >
                <div className="divide-y divide-border">
                  {seatRows.map((s) => (
                    <Link
                      key={s.code}
                      to={`/political-landscape/candidates?seatType=${s.code}`}
                      className="px-4 py-2 flex items-baseline gap-3 hover:bg-muted/40"
                    >
                      <span className="text-erp-sm font-medium">{seatTypeLabel(s.code)}</span>
                      <span className="text-erp-xs text-muted-foreground">
                        {s.level ?? 'no level published'}
                      </span>
                      <span className="ml-auto text-erp-sm tabular-nums">{count(s.n)}</span>
                    </Link>
                  ))}
                  {seatRows.length === 0 && (
                    <p className="px-4 py-6 text-erp-sm text-muted-foreground">
                      The seat-type list could not be read, so there is nothing to break the
                      register down by.
                    </p>
                  )}
                </div>
                {!candidatesScanned && (
                  <div className="px-4 py-3">
                    <Note tone="info">
                      Counted over the first {candidateRows.length} of {count(candidateTotal)}{' '}
                      candidates — there is no group-by endpoint, so this is a scan rather than a
                      tally. Filter the Candidates screen for an exact figure per seat.
                    </Note>
                  </div>
                )}
              </Panel>
            </div>
          </div>
        </>
      )}

      <PartyFormDialog
        open={creating}
        party={null}
        onClose={() => setCreating(false)}
        onSaved={(saved) => {
          setCreating(false)
          // Straight to the new party's panel, where the next thing — a coalition
          // membership — is recorded.
          navigate(`/political-landscape/parties?party=${saved.id}`)
        }}
      />
    </div>
  )
}
